import { House, LayoutGrid } from 'lucide-react';
import type { ComponentType } from 'react';
import { NavLink } from 'react-router';
import { pinnedModules } from '../../data/access';
import { useModuleStates, useUsable } from '../../data/hooks';
import { getWebModule, listWebModules, modulePath } from '../../modules/registry';
import { cn } from '../../ui/cn';
import { Logo } from '../../ui/Logo';

type Item = {
  to: string;
  label: string;
  Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  end: boolean;
};

/**
 * Today, the pinned modules, Modules. Only modules that are on. The bottom bar has room for three
 * pins; the side nav (from 1024px) lists the pins first, then every other module that is on.
 */
function useNavItems(all = false): Item[] {
  const usable = useUsable();
  const { data: states } = useModuleStates();
  const registered = listWebModules().map((m) => m.id);
  const pinned = pinnedModules(usable, states ?? [], registered);
  const ids = all
    ? [
        ...pinned,
        ...pinnedModules(usable, states ?? [], registered, Infinity).filter(
          (id) => !pinned.includes(id),
        ),
        ...registered.filter((id) => id !== 'today' && usable(id) && !pinned.includes(id)),
      ].filter((id, i, list) => list.indexOf(id) === i)
    : pinned;
  return [
    { to: '/', label: 'Today', Icon: House, end: true },
    ...ids.map((id) => {
      const m = getWebModule(id)!;
      return { to: modulePath(id), label: m.navLabel, Icon: m.Icon, end: false };
    }),
    { to: '/modules', label: 'Modules', Icon: LayoutGrid, end: false },
  ];
}

/** Rounded pill bar (design.md) on phones and tablets; from 1024px the side nav takes over. */
export function BottomNav() {
  const items = useNavItems();
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-4 bottom-[calc(12px+env(safe-area-inset-bottom))] z-30 mx-auto max-w-lg lg:hidden"
    >
      <ul className="flex justify-around rounded-full bg-surface-2 p-1.5">
        {items.map((it) => (
          <li key={it.to}>
            <NavLink
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 min-w-14 flex-col items-center justify-center rounded-full px-3 text-[11px] font-medium hover:bg-secondary',
                  isActive ? 'text-primary' : 'text-muted hover:text-fg',
                )
              }
            >
              <it.Icon aria-hidden className="h-5 w-5" />
              {it.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** From 1024px: the same items as the bottom bar, down the left side. */
export function SideNav() {
  const items = useNavItems(true);
  return (
    <nav
      aria-label="Main"
      className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col gap-8 border-r border-divider bg-background px-4 py-7 lg:flex"
    >
      <div className="px-3">
        <Logo width={88} />
      </div>
      <ul className="flex flex-col gap-1">
        {items.map((it) => (
          <li key={it.to}>
            <NavLink
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 items-center gap-3 rounded-full px-3 text-[15px] font-medium',
                  isActive
                    ? 'bg-surface-2 text-primary'
                    : 'text-muted hover:bg-surface hover:text-fg',
                )
              }
            >
              <it.Icon aria-hidden className="h-5 w-5" />
              {it.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
